import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import config from '../config';
import {
  VOICE_GENDERS,
  VOICE_PROFILES,
  getStoredVoicePreferences,
  saveVoicePreferences,
} from '../voicePreferences';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  BUILT_IN_AVATARS,
  CUSTOM_AVATAR_ID,
  DEFAULT_AVATAR_ID,
  findAvatarById,
  isKnownAvatarId,
  readStoredAvatar,
  storeCustomAvatarUrl,
  storeSelectedAvatar,
} from '../avatarCatalog';

// Built-in avatars (incl. body type and matching voice) live in avatarCatalog.js.
const DEFAULT_AVATAR_OPTIONS = BUILT_IN_AVATARS;

// "My Own Avatar" (Avaturn selfie flow) and "Upload GLB" are switched off for
// now; caregivers choose from the built-in avatars only. The code is kept:
// set this to true to offer them again.
const SHOW_CUSTOM_AVATAR_TOOLS = false;

// Larger type for the settings controls (caregivers are often older adults).
const SELECT_SX = { fontSize: '1.45rem', fontWeight: 600, color: '#0f3950' };
const LABEL_SX = {
  fontSize: '1.45rem',
  fontWeight: 700,
  color: '#1a4d67',
  '&.MuiInputLabel-shrink': { transform: 'translate(14px, -13px) scale(0.75)' },
};
const MENU_ITEM_SX = { fontSize: '1.25rem', py: 1.25 };
const MAX_CUSTOM_AVATAR_MB = 15;
const AVATURN_SDK_MODULE_URLS = [
  process.env.REACT_APP_AVATURN_SDK_URL,
  'https://cdn.jsdelivr.net/npm/@avaturn/sdk/dist/index.js',
  'https://unpkg.com/@avaturn/sdk/dist/index.js?module',
].filter(Boolean);
const AVATURN_PROJECT_URL = process.env.REACT_APP_AVATURN_URL || 'https://vica.avaturn.dev';
const AVATURN_IMPORT_TIMEOUT_MS = 15000;
const AVATURN_INIT_TIMEOUT_MS = 30000;
const AVATURN_CONTAINER_WAIT_TIMEOUT_MS = 5000;
const AVATURN_CONTAINER_POLL_MS = 80;

function createCustomAvatarOption(url) {
  return {
    id: CUSTOM_AVATAR_ID,
    label: 'My Avatar',
    url,
  };
}

function isLikelyGlbHttpUrl(url) {
  if (typeof url !== 'string' || !url.trim()) {
    return false;
  }

  try {
    const parsed = new URL(url.trim());
    return (
      (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
      /\.glb$/i.test(parsed.pathname)
    );
  } catch {
    return false;
  }
}

function isHttpUrl(url) {
  if (typeof url !== 'string' || !url.trim()) {
    return false;
  }

  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

function isDataUrl(value) {
  return typeof value === 'string' && /^data:/i.test(value.trim());
}

function classifyAvatarExportSource(url, urlTypeHint) {
  if (typeof url !== 'string') {
    return null;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  if (urlTypeHint === 'dataURL' || isDataUrl(trimmed)) {
    return { type: 'dataURL', url: trimmed };
  }

  if (urlTypeHint === 'httpURL' || isLikelyGlbHttpUrl(trimmed) || isHttpUrl(trimmed)) {
    return { type: 'httpURL', url: trimmed };
  }

  return null;
}

function extractAvatarExportSource(payload) {
  const directCandidates = [
    { url: payload?.url, urlType: payload?.urlType },
    { url: payload?.avatar?.url, urlType: payload?.avatar?.urlType },
    { url: payload?.model?.url, urlType: payload?.model?.urlType },
    { url: payload?.glbUrl, urlType: 'httpURL' },
    { url: payload?.avatarUrl, urlType: 'httpURL' },
  ];

  for (const candidate of directCandidates) {
    const source = classifyAvatarExportSource(candidate.url, candidate.urlType);
    if (source) {
      return source;
    }
  }

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const visited = new Set();
  const queue = [payload];

  while (queue.length) {
    const current = queue.shift();
    if (!current) {
      continue;
    }

    if (typeof current === 'string') {
      const source = classifyAvatarExportSource(current);
      if (source) {
        return source;
      }
      continue;
    }

    if (typeof current !== 'object' || visited.has(current)) {
      continue;
    }
    visited.add(current);

    if (!Array.isArray(current)) {
      const source = classifyAvatarExportSource(current.url, current.urlType);
      if (source) {
        return source;
      }
    }

    Object.values(current).forEach((value) => queue.push(value));
  }

  return null;
}

function convertDataUrlToGlbFile(dataUrl, fileName = 'my-avatar.glb') {
  if (!isDataUrl(dataUrl)) {
    return null;
  }

  const separatorIndex = dataUrl.indexOf(',');
  if (separatorIndex < 0) {
    return null;
  }

  const metadata = dataUrl.slice(5, separatorIndex);
  const encodedPayload = dataUrl.slice(separatorIndex + 1);
  const isBase64Payload = /;base64/i.test(metadata);
  const mimeTypeMatch = metadata.match(/^([^;,]+)/i);
  const mimeType = mimeTypeMatch?.[1] || 'model/gltf-binary';

  let bytes;
  try {
    if (isBase64Payload) {
      const binary = atob(encodedPayload);
      bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
    } else {
      const decoded = decodeURIComponent(encodedPayload);
      bytes = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i += 1) {
        bytes[i] = decoded.charCodeAt(i);
      }
    }
  } catch {
    return null;
  }

  if (bytes.length < 4) {
    return null;
  }

  const glbHeader = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
  if (glbHeader !== 'glTF') {
    return null;
  }

  if (typeof File === 'function') {
    return new File([bytes], fileName, { type: mimeType });
  }

  const blob = new Blob([bytes], { type: mimeType });
  blob.name = fileName;
  return blob;
}

function hasWebGLSupport() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const canvas = document.createElement('canvas');
    const context =
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return !!(window.WebGLRenderingContext && context);
  } catch (error) {
    return false;
  }
}

function withTimeout(promise, timeoutMs, timeoutMessage) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

function waitForContainer(getter, timeoutMs = AVATURN_CONTAINER_WAIT_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const tick = () => {
      const element = getter();
      if (element) {
        resolve(element);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('Avatar creator container did not mount. Please reopen and try again.'));
        return;
      }

      setTimeout(tick, AVATURN_CONTAINER_POLL_MS);
    };

    tick();
  });
}

async function loadAvaturnSdkModule() {
  const attemptedUrls = [...new Set(AVATURN_SDK_MODULE_URLS.map((url) => String(url).trim()))].filter(
    Boolean
  );
  const failed = [];

  for (const moduleUrl of attemptedUrls) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const sdkModule = await withTimeout(
        import(/* webpackIgnore: true */ moduleUrl),
        AVATURN_IMPORT_TIMEOUT_MS,
        `Timed out while loading Avaturn SDK from ${moduleUrl}`
      );
      return sdkModule;
    } catch (error) {
      failed.push(`${moduleUrl}: ${error?.message || 'Unknown import error'}`);
      console.warn(`Failed to load Avaturn SDK from ${moduleUrl}`, error);
    }
  }

  throw new Error(`Avaturn SDK could not be loaded. ${failed.join(' | ')}`);
}

function EnvironmentSettings({ open, onClose, asPage = false }) {
  const [envType, setEnvType] = useState('avatar');
  const [webglSupported, setWebglSupported] = useState(true);
  const [modelViewerReady, setModelViewerReady] = useState(false);
  const [customAvatarOption, setCustomAvatarOption] = useState(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isDeletingAvatar, setIsDeletingAvatar] = useState(false);
  const [isImportingAvatar, setIsImportingAvatar] = useState(false);
  const [isExportingAvatar, setIsExportingAvatar] = useState(false);
  const [avatarNotice, setAvatarNotice] = useState('');
  const [isAvaturnDialogOpen, setAvaturnDialogOpen] = useState(false);
  const [isAvaturnReady, setAvaturnReady] = useState(false);
  const [avaturnStatus, setAvaturnStatus] = useState('');
  const avatarFileInputRef = useRef(null);
  const avaturnContainerRef = useRef(null);
  const avaturnSdkRef = useRef(null);
  const handledExportFingerprintRef = useRef('');
  const userId = localStorage.getItem('userId');
  const isVisible = asPage || open;

  const avatarOptions = useMemo(() => {
    const options = [...DEFAULT_AVATAR_OPTIONS];
    if (customAvatarOption) {
      options.unshift(customAvatarOption);
    }
    return options;
  }, [customAvatarOption]);
  const customAvatarUrl = customAvatarOption?.url || '';
  const hasCustomAvatar = Boolean(customAvatarUrl);

  const isAvailableAvatarId = useCallback(
    (id) => avatarOptions.some((avatar) => avatar.id === id),
    [avatarOptions]
  );

  const backgroundOptions = useMemo(
    () => [
      'https://storage.googleapis.com/vicaassets/1.png',
      'https://storage.googleapis.com/vicaassets/2.png',
      'https://storage.googleapis.com/vicaassets/3.png',
      'https://storage.googleapis.com/vicaassets/4.png',
      'https://storage.googleapis.com/vicaassets/5.png',
      'https://storage.googleapis.com/vicaassets/6.png',
      'https://storage.googleapis.com/vicaassets/7.png',
      'https://storage.googleapis.com/vicaassets/8.png',
      'https://storage.googleapis.com/vicaassets/9.png',
      'https://storage.googleapis.com/vicaassets/10.png',
      'https://storage.googleapis.com/vicaassets/11.png',
      'https://storage.googleapis.com/vicaassets/12.png',
      'https://storage.googleapis.com/vicaassets/13.png',
      'https://storage.googleapis.com/vicaassets/15.png',
    ],
    []
  );

  // Avatar ID (see avatarCatalog.js), not a file address.
  const [selectedAvatar, setSelectedAvatar] = useState(() => readStoredAvatar() || DEFAULT_AVATAR_ID);
  const [selectedBackground, setSelectedBackground] = useState(
    () => localStorage.getItem('background') || backgroundOptions[0]
  );
  const [voiceGender, setVoiceGender] = useState(() => getStoredVoicePreferences().gender);
  const [voiceProfile, setVoiceProfile] = useState(() => getStoredVoicePreferences().profile.value);
  const persistedPreferencesRef = useRef({
    avatar: readStoredAvatar() || DEFAULT_AVATAR_ID,
    background: localStorage.getItem('background') || backgroundOptions[0],
    voiceGender: getStoredVoicePreferences().gender,
    voiceProfile: getStoredVoicePreferences().profile.value,
  });
  const persistInFlightRef = useRef(false);
  const queuedPreferencesRef = useRef(null);
  const persistPreferencesRef = useRef(async () => {});

  const persistEnvironmentPreferences = useCallback(
    async (overrides = {}) => {
      const nextState = {
        avatar: overrides.avatar ?? selectedAvatar,
        background: overrides.background ?? selectedBackground,
        voiceGender: overrides.voiceGender ?? voiceGender,
        voiceProfile: overrides.voiceProfile ?? voiceProfile,
      };

      const previousState = persistedPreferencesRef.current;
      const hasChanged =
        previousState.avatar !== nextState.avatar ||
        previousState.background !== nextState.background ||
        previousState.voiceGender !== nextState.voiceGender ||
        previousState.voiceProfile !== nextState.voiceProfile;

      if (!hasChanged) {
        return;
      }

      localStorage.setItem('background', nextState.background);
      saveVoicePreferences({ gender: nextState.voiceGender, profileValue: nextState.voiceProfile });
      // After the voice is saved, so a newly chosen avatar loads with its voice.
      storeSelectedAvatar(nextState.avatar);

      if (persistInFlightRef.current) {
        queuedPreferencesRef.current = nextState;
        return;
      }

      persistInFlightRef.current = true;

      try {
        if (userId && nextState.avatar) {
          await axios.post(`${config.API_BASE_URL}/user-avatar/select`, {
            userId,
            selectedAvatar: nextState.avatar,
          });
        }

        persistedPreferencesRef.current = nextState;
      } catch (error) {
        console.error('Failed to save environment settings:', error);
      } finally {
        persistInFlightRef.current = false;

        if (queuedPreferencesRef.current) {
          const queuedState = queuedPreferencesRef.current;
          queuedPreferencesRef.current = null;
          setTimeout(() => {
            void persistPreferencesRef.current(queuedState);
          }, 0);
        }
      }
    },
    [selectedAvatar, selectedBackground, voiceGender, voiceProfile, userId]
  );

  useEffect(() => {
    persistPreferencesRef.current = persistEnvironmentPreferences;
  }, [persistEnvironmentPreferences]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const storedVoicePreferences = getStoredVoicePreferences();
    setVoiceGender(storedVoicePreferences.gender);
    setVoiceProfile(storedVoicePreferences.profile.value);
    persistedPreferencesRef.current = {
      avatar: readStoredAvatar() || DEFAULT_AVATAR_ID,
      background: localStorage.getItem('background') || backgroundOptions[0],
      voiceGender: storedVoicePreferences.gender,
      voiceProfile: storedVoicePreferences.profile.value,
    };
  }, [isVisible, backgroundOptions]);

  useEffect(() => {
    let canceled = false;
    const webglAvailable = hasWebGLSupport();
    setWebglSupported(webglAvailable);

    if (!webglAvailable) {
      setModelViewerReady(false);
      return () => {
        canceled = true;
      };
    }

    import('@google/model-viewer')
      .then(() => {
        if (!canceled) {
          setModelViewerReady(true);
        }
      })
      .catch((error) => {
        console.error('Failed to load model-viewer:', error);
        if (!canceled) {
          setModelViewerReady(false);
          setWebglSupported(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    if (!isVisible || !userId) {
      return;
    }

    let canceled = false;

    axios
      .get(`${config.API_BASE_URL}/user-avatar`, {
        params: { userId },
      })
      .then((res) => {
        if (canceled) {
          return;
        }

        const avatarUrl = typeof res.data?.avatarUrl === 'string' ? res.data.avatarUrl : '';
        const savedId = typeof res.data?.selectedAvatar === 'string' ? res.data.selectedAvatar : '';

        if (avatarUrl) {
          setCustomAvatarOption(createCustomAvatarOption(avatarUrl));
        } else {
          setCustomAvatarOption(null);
        }
        storeCustomAvatarUrl(avatarUrl);

        const savedIsUsable =
          isKnownAvatarId(savedId) && (savedId !== CUSTOM_AVATAR_ID || Boolean(avatarUrl));
        if (savedIsUsable) {
          setSelectedAvatar(savedId);
          storeSelectedAvatar(savedId);
          persistedPreferencesRef.current = {
            ...persistedPreferencesRef.current,
            avatar: savedId,
          };
        }
      })
      .catch((error) => {
        console.error('Failed to load user avatar:', error);
      });

    return () => {
      canceled = true;
    };
  }, [isVisible, userId]);

  const applyImportedAvatar = useCallback((uploadedAvatarUrl, successMessage) => {
    const avatarOption = createCustomAvatarOption(uploadedAvatarUrl);
    setCustomAvatarOption(avatarOption);
    storeCustomAvatarUrl(uploadedAvatarUrl);
    setSelectedAvatar(CUSTOM_AVATAR_ID);
    storeSelectedAvatar(CUSTOM_AVATAR_ID);
    setAvatarNotice(successMessage);
    void persistPreferencesRef.current({ avatar: CUSTOM_AVATAR_ID });
  }, []);

  const uploadAvatarFile = useCallback(
    async (file, successMessage = 'My Avatar uploaded and selected.') => {
      if (!file) {
        throw new Error('Avatar file is required.');
      }

      const fileName = typeof file.name === 'string' && file.name.trim() ? file.name : 'my-avatar.glb';

      if (!/\.glb$/i.test(fileName)) {
        throw new Error('Please upload a .glb file.');
      }

      if (file.size > MAX_CUSTOM_AVATAR_MB * 1024 * 1024) {
        throw new Error(`Avatar file is too large. Max allowed is ${MAX_CUSTOM_AVATAR_MB}MB.`);
      }

      if (!userId) {
        throw new Error('Please sign in again to upload a custom avatar.');
      }

      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('avatar', file, fileName);

      const uploadResponse = await axios.post(`${config.API_BASE_URL}/user-avatar/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const uploadedAvatarUrl = uploadResponse.data?.avatarUrl;
      if (!uploadedAvatarUrl || typeof uploadedAvatarUrl !== 'string') {
        throw new Error('Backend did not return uploaded avatar URL');
      }

      applyImportedAvatar(uploadedAvatarUrl, successMessage);
      return uploadedAvatarUrl;
    },
    [applyImportedAvatar, userId]
  );

  const importAvatarFromUrl = useCallback(
    async (sourceUrl) => {
      if (!userId) {
        throw new Error('Please sign in again to import a custom avatar.');
      }

      const response = await axios.post(`${config.API_BASE_URL}/user-avatar/import-url`, {
        userId,
        sourceUrl,
      });

      const uploadedAvatarUrl = response.data?.avatarUrl;
      if (!uploadedAvatarUrl || typeof uploadedAvatarUrl !== 'string') {
        throw new Error('Backend did not return imported avatar URL');
      }

      applyImportedAvatar(uploadedAvatarUrl, 'My Avatar created and selected.');
      return uploadedAvatarUrl;
    },
    [applyImportedAvatar, userId]
  );

  const importAvatarFromDataUrl = useCallback(
    async (dataUrl) => {
      const avatarFile = convertDataUrlToGlbFile(dataUrl, `avaturn-${Date.now()}.glb`);
      if (!avatarFile) {
        throw new Error('Avaturn export did not include a valid GLB payload.');
      }

      return uploadAvatarFile(avatarFile, 'My Avatar created and selected.');
    },
    [uploadAvatarFile]
  );

  const processAvaturnExportPayload = useCallback(
    async (payload) => {
      if (payload && payload.avatarSupportsFaceAnimations === false) {
        setAvaturnStatus(
          'This export is not T2 (no face blendshapes). Please create/export a T2 avatar, then try again.'
        );
        return;
      }

      const exportSource = extractAvatarExportSource(payload);
      if (!exportSource) {
        setAvaturnStatus(
          'Avatar export completed, but no GLB payload was returned. Retry export or use Upload GLB.'
        );
        return;
      }

      const fingerprint = `${exportSource.type}:${exportSource.url.slice(0, 128)}`;
      if (handledExportFingerprintRef.current === fingerprint) {
        return;
      }
      handledExportFingerprintRef.current = fingerprint;

      try {
        setIsImportingAvatar(true);
        setAvaturnStatus(
          payload?.avatarSupportsFaceAnimations === true
            ? 'T2 verified. Importing avatar to your profile...'
            : 'Importing avatar to your profile...'
        );
        if (exportSource.type === 'dataURL') {
          setAvaturnStatus(
            payload?.avatarSupportsFaceAnimations === true
              ? 'T2 verified. Uploading avatar to your profile...'
              : 'Uploading avatar to your profile...'
          );
          await importAvatarFromDataUrl(exportSource.url);
        } else {
          setAvaturnStatus(
            payload?.avatarSupportsFaceAnimations === true
              ? 'T2 verified. Importing avatar to your profile...'
              : 'Importing avatar to your profile...'
          );
          await importAvatarFromUrl(exportSource.url);
        }

        setAvaturnStatus('Avatar imported successfully.');
        setAvaturnDialogOpen(false);
      } catch (error) {
        handledExportFingerprintRef.current = '';
        console.error('Failed to import Avaturn export:', error);
        setAvaturnStatus(
          error?.response?.data?.error || error?.message || 'Failed to import exported avatar.'
        );
      } finally {
        setIsImportingAvatar(false);
      }
    },
    [importAvatarFromDataUrl, importAvatarFromUrl]
  );

  const handleRunAvaturnExport = useCallback(async () => {
    if (!avaturnSdkRef.current) {
      setAvaturnStatus('Avatar creator is still loading. Please wait a moment.');
      return;
    }

    const exportAvatarFn = avaturnSdkRef.current.exportAvatar;
    if (typeof exportAvatarFn !== 'function') {
      setAvaturnStatus('Use the Next button inside Avaturn to export this avatar.');
      return;
    }

    try {
      setIsExportingAvatar(true);
      setAvaturnStatus('Generating GLB from your avatar...');
      const payload = await exportAvatarFn.call(avaturnSdkRef.current);
      if (payload) {
        await processAvaturnExportPayload(payload);
      }
    } catch (error) {
      console.error('Failed to export avatar from Avaturn SDK:', error);
      setAvaturnStatus(
        error?.message || 'Failed to export avatar. You can still tap Next inside Avaturn.'
      );
    } finally {
      setIsExportingAvatar(false);
    }
  }, [processAvaturnExportPayload]);

  const handleCreateWithAvaturn = useCallback(() => {
    setAvatarNotice('');

    if (!userId) {
      setAvatarNotice('Please sign in again to create a custom avatar.');
      return;
    }

    handledExportFingerprintRef.current = '';
    setAvaturnReady(false);
    setAvaturnStatus('Loading avatar creator...');
    setAvaturnDialogOpen(true);
  }, [userId]);

  const handleCloseAvaturnDialog = useCallback(() => {
    if (isImportingAvatar || isExportingAvatar) {
      return;
    }
    setAvaturnDialogOpen(false);
  }, [isExportingAvatar, isImportingAvatar]);

  const handleOpenAvaturnExternally = useCallback(() => {
    const targetUrl = AVATURN_PROJECT_URL;
    if (typeof window !== 'undefined') {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  useEffect(() => {
    if (!isAvaturnDialogOpen) {
      return;
    }

    let containerElement = null;
    let canceled = false;
    let sdkInstance = null;

    const bootstrapAvaturn = async () => {
      try {
        setAvaturnReady(false);
        setAvaturnStatus('Loading avatar creator...');

        containerElement = await waitForContainer(() => avaturnContainerRef.current);
        if (canceled || !containerElement) {
          return;
        }

        const sdkModule = await loadAvaturnSdkModule();
        const AvaturnSDKClass =
          sdkModule?.AvaturnSDK ||
          sdkModule?.default?.AvaturnSDK ||
          sdkModule?.default;

        if (!AvaturnSDKClass) {
          throw new Error('Avaturn SDK failed to load');
        }

        sdkInstance = new AvaturnSDKClass();
        avaturnSdkRef.current = sdkInstance;

        setAvaturnStatus('Connecting avatar creator...');
        await withTimeout(
          sdkInstance.init(containerElement, {
            url: AVATURN_PROJECT_URL,
          }),
          AVATURN_INIT_TIMEOUT_MS,
          `Timed out while connecting to ${AVATURN_PROJECT_URL}`
        );

        if (canceled) {
          return;
        }

        setAvaturnReady(true);
        setAvaturnStatus(
          'Step 1: take selfies. Step 2: customize avatar. Step 3: click "Generate GLB & Save".'
        );

        sdkInstance.on('export', async (payload) => {
          if (canceled) {
            return;
          }

          await processAvaturnExportPayload(payload);
        });
      } catch (error) {
        if (canceled) {
          return;
        }
        console.error('Failed to initialize Avaturn creator:', error);
        setAvaturnStatus(
          error?.message ||
            `Unable to open Avaturn creator. Check network and project URL (${AVATURN_PROJECT_URL}), then try again.`
        );
      }
    };

    bootstrapAvaturn();

    return () => {
      canceled = true;

      if (sdkInstance && typeof sdkInstance.destroy === 'function') {
        try {
          sdkInstance.destroy();
        } catch (error) {
          console.warn('Failed to destroy Avaturn SDK instance:', error);
        }
      }
      setAvaturnReady(false);
      avaturnSdkRef.current = null;

      if (containerElement) {
        containerElement.innerHTML = '';
      }
    };
  }, [isAvaturnDialogOpen, processAvaturnExportPayload]);

  useEffect(() => {
    if (!selectedAvatar) {
      return;
    }

    if (!isAvailableAvatarId(selectedAvatar)) {
      const fallbackAvatar = DEFAULT_AVATAR_ID;
      setSelectedAvatar(fallbackAvatar);
      storeSelectedAvatar(fallbackAvatar);
      persistedPreferencesRef.current = {
        ...persistedPreferencesRef.current,
        avatar: fallbackAvatar,
      };
    }
  }, [isAvailableAvatarId, selectedAvatar]);

  const handleAvatarUploadClick = useCallback(() => {
    setAvatarNotice('');

    if (!userId) {
      setAvatarNotice('Please sign in again to upload a custom avatar.');
      return;
    }

    avatarFileInputRef.current?.click();
  }, [userId]);

  const handleDeleteCustomAvatar = useCallback(async () => {
    setAvatarNotice('');

    if (!hasCustomAvatar) {
      setAvatarNotice('No custom avatar found to delete.');
      return;
    }

    if (!userId) {
      setAvatarNotice('Please sign in again to delete your custom avatar.');
      return;
    }

    const confirmed = window.confirm('Delete your custom avatar from your profile?');
    if (!confirmed) {
      return;
    }

    try {
      setIsDeletingAvatar(true);
      setAvatarNotice('Deleting your custom avatar...');

      await axios.post(`${config.API_BASE_URL}/user-avatar/delete`, { userId });

      setCustomAvatarOption(null);
      storeCustomAvatarUrl('');

      if (selectedAvatar === CUSTOM_AVATAR_ID) {
        const fallbackAvatar = DEFAULT_AVATAR_ID;
        setSelectedAvatar(fallbackAvatar);
        storeSelectedAvatar(fallbackAvatar);
        persistedPreferencesRef.current = {
          ...persistedPreferencesRef.current,
          avatar: fallbackAvatar,
        };

        try {
          await axios.post(`${config.API_BASE_URL}/user-avatar/select`, {
            userId,
            selectedAvatar: fallbackAvatar,
          });
        } catch (saveError) {
          console.warn('Failed to persist fallback avatar after delete:', saveError);
        }
      }

      setAvatarNotice('Custom avatar deleted.');
    } catch (error) {
      console.error('Avatar delete failed:', error);
      setAvatarNotice(
        error?.response?.data?.error || error?.message || 'Failed to delete custom avatar.'
      );
    } finally {
      setIsDeletingAvatar(false);
    }
  }, [hasCustomAvatar, selectedAvatar, userId]);

  const handleAvatarFileChange = useCallback(
    async (event) => {
      const input = event.target;
      const selectedFile = input.files?.[0];
      input.value = '';

      if (!selectedFile) {
        return;
      }

      try {
        setIsUploadingAvatar(true);
        setAvatarNotice('Uploading avatar...');
        await uploadAvatarFile(selectedFile, 'My Avatar uploaded and selected.');
      } catch (error) {
        console.error('Avatar upload failed:', error);
        setAvatarNotice(
          error?.response?.data?.error ||
            error?.message ||
            'Avatar upload failed. Please try another file.'
        );
      } finally {
        setIsUploadingAvatar(false);
      }
    },
    [uploadAvatarFile]
  );

  const handleSelectAvatar = (avatarId) => {
    setSelectedAvatar(avatarId);
    // A built-in avatar carries its own voice gender so the voice matches the
    // character; the caregiver can still change it in Voice Preferences.
    const builtIn = findAvatarById(avatarId);
    if (builtIn?.voiceGender) {
      setVoiceGender(builtIn.voiceGender);
      void persistPreferencesRef.current({ avatar: avatarId, voiceGender: builtIn.voiceGender });
      return;
    }
    void persistPreferencesRef.current({ avatar: avatarId });
  };

  const handleSelectBackground = (backgroundUrl) => {
    setSelectedBackground(backgroundUrl);
    void persistPreferencesRef.current({ background: backgroundUrl });
  };

  const handleVoiceGenderChange = (event) => {
    const value = event.target.value;
    setVoiceGender(value);
    void persistPreferencesRef.current({ voiceGender: value });
  };

  const handleVoiceProfileChange = (event) => {
    const value = event.target.value;
    setVoiceProfile(value);
    void persistPreferencesRef.current({ voiceProfile: value });
  };

  const settingsContent = (
    <>
      <FormControl fullWidth margin="dense">
        <InputLabel id="env-type-label" sx={LABEL_SX}>Setting</InputLabel>
        <Select
          labelId="env-type-label"
          value={envType}
          label="Setting"
          onChange={(e) => setEnvType(e.target.value)}
          sx={SELECT_SX}
        >
          <MenuItem value="avatar" sx={MENU_ITEM_SX}>Avatar</MenuItem>
          <MenuItem value="background" sx={MENU_ITEM_SX}>Background</MenuItem>
          <MenuItem value="voice" sx={MENU_ITEM_SX}>Voice</MenuItem>
        </Select>
      </FormControl>

      {envType === 'avatar' && (
        <FormControl fullWidth margin="dense">
          <h4 className="mt-4 mb-2 text-[1.6rem] font-extrabold text-[#0f3950]">Select Avatar</h4>
          {!webglSupported && (
            <p className="text-[1.15rem] leading-7 text-[#355c71] mb-3">
              3D preview is unavailable because WebGL is disabled. You can still select an avatar.
            </p>
          )}
          {SHOW_CUSTOM_AVATAR_TOOLS && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <button
              type="button"
              className="vica-btn vica-btn-sm"
              onClick={handleCreateWithAvaturn}
              disabled={
                isUploadingAvatar ||
                isDeletingAvatar ||
                isImportingAvatar ||
                isExportingAvatar
              }
            >
              {isImportingAvatar || isExportingAvatar ? 'Processing...' : 'My Own Avatar'}
            </button>
            <button
              type="button"
              className="vica-btn-secondary vica-btn-sm"
              onClick={handleAvatarUploadClick}
              disabled={
                isUploadingAvatar ||
                isDeletingAvatar ||
                isImportingAvatar ||
                isExportingAvatar
              }
            >
              {isUploadingAvatar ? 'Uploading...' : 'Upload GLB'}
            </button>
            {hasCustomAvatar && (
              <button
                type="button"
                className="vica-btn-danger vica-btn-sm"
                onClick={handleDeleteCustomAvatar}
                disabled={
                  isUploadingAvatar ||
                  isDeletingAvatar ||
                  isImportingAvatar ||
                  isExportingAvatar
                }
              >
                {isDeletingAvatar ? 'Deleting...' : 'Delete My Avatar'}
              </button>
            )}
            <span className="text-xs text-[#4d6f82]">
              Create via selfie flow in Avaturn, or upload a TalkingHead-compatible `.glb`.
            </span>
          </div>
          )}
          <input
            ref={avatarFileInputRef}
            type="file"
            accept=".glb,model/gltf-binary"
            style={{ display: 'none' }}
            onChange={handleAvatarFileChange}
          />
          {avatarNotice && (
            <p className="text-[1.15rem] leading-7 text-[#355c71] mb-3">{avatarNotice}</p>
          )}
          <Grid container spacing={2}>
            {avatarOptions.map((avatar, index) => (
              <Grid item xs={6} md={4} key={avatar.id}>
                <div
                  onClick={() => handleSelectAvatar(avatar.id)}
                  className={`cursor-pointer rounded-xl transition-all duration-200 border-2 w-full p-2 bg-white ${
                    selectedAvatar === avatar.id
                      ? 'border-[#006298] shadow-lg shadow-sky-100'
                      : 'border-transparent hover:border-sky-200'
                  }`}
                >
                  {modelViewerReady ? (
                    <model-viewer
                      src={avatar.url}
                      alt="Avatar preview"
                      camera-orbit="0deg 75deg 1m"
                      camera-target="0m 1.3m 0m"
                      style={{ width: '100%', height: '220px', display: 'block' }}
                    />
                  ) : (
                    <div
                      className="w-full h-[220px] rounded-lg flex items-center justify-center text-center text-[1.3rem] font-bold text-[#2c5b74]"
                      style={{
                        background:
                          'linear-gradient(145deg, rgba(234,246,255,1), rgba(217,234,246,1))',
                      }}
                    >
                      {avatar.label || `Avatar ${index + 1}`}
                    </div>
                  )}
                  {modelViewerReady && (
                    <div className="mt-2 mb-1 text-[1.35rem] text-[#0f3950] font-bold text-center">
                      {avatar.label}
                    </div>
                  )}
                </div>
              </Grid>
            ))}
          </Grid>
        </FormControl>
      )}

      {envType === 'background' && (
        <FormControl fullWidth margin="dense">
          <h4 className="mt-4 mb-3 text-[1.6rem] font-extrabold text-[#0f3950]">Select Background</h4>
          <Grid container spacing={2}>
            {backgroundOptions.map((url) => (
              <Grid item xs={6} md={4} key={url}>
                <img
                  src={url}
                  alt="Background option"
                  className={`w-full h-[150px] object-cover cursor-pointer border-2 rounded-xl ${
                    selectedBackground === url
                      ? 'border-[#006298] shadow-md shadow-sky-100'
                      : 'border-transparent hover:border-sky-200'
                  }`}
                  onClick={() => handleSelectBackground(url)}
                />
              </Grid>
            ))}
          </Grid>
        </FormControl>
      )}

      {envType === 'voice' && (
        <FormControl fullWidth margin="dense">
          <h4 className="mt-4 mb-2 text-[1.6rem] font-extrabold text-[#0f3950]">Voice Preferences</h4>
          <p className="text-[1.15rem] leading-7 text-[#4d6f82] mb-4">
            Choose the avatar voice style and English accent.
          </p>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="voice-gender-label" sx={LABEL_SX}>Voice</InputLabel>
                <Select
                  labelId="voice-gender-label"
                  value={voiceGender}
                  label="Voice"
                  onChange={handleVoiceGenderChange}
                  onBlur={() => void persistPreferencesRef.current()}
                  sx={SELECT_SX}
                >
                  {VOICE_GENDERS.map((voiceOption) => (
                    <MenuItem key={voiceOption.value} value={voiceOption.value} sx={MENU_ITEM_SX}>
                      {voiceOption.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="voice-profile-label" sx={LABEL_SX}>Nationality</InputLabel>
                <Select
                  labelId="voice-profile-label"
                  value={voiceProfile}
                  label="Nationality"
                  onChange={handleVoiceProfileChange}
                  onBlur={() => void persistPreferencesRef.current()}
                  sx={SELECT_SX}
                >
                  {VOICE_PROFILES.map((profileOption) => (
                    <MenuItem key={profileOption.value} value={profileOption.value} sx={MENU_ITEM_SX}>
                      {profileOption.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </FormControl>
      )}
    </>
  );

  return (
    <>
    {asPage ? (
      <div className="h-full overflow-auto">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h1 className="vica-title !text-[2.2rem] m-0">Environment Settings</h1>
          <div className="flex items-center gap-4">
            <button
              className="vica-btn-secondary vica-session-btn-new shrink-0 justify-center"
              onClick={onClose}
            >
              <ArrowBackIcon sx={{ fontSize: 28 }} />
              Back
            </button>
          </div>
        </div>
        <div className="p-0">
          {settingsContent}
        </div>
      </div>
    ) : (
      <Dialog
        open={isVisible}
        onClose={onClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f3d56' }}>Environment Settings</DialogTitle>
        <DialogContent sx={{ background: 'linear-gradient(180deg, rgba(236,247,255,0.58), #ffffff)' }}>
          {settingsContent}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <button className="vica-btn-secondary vica-btn-md" onClick={onClose}>
            Close
          </button>
        </DialogActions>
      </Dialog>
    )}
    <Dialog
      open={isAvaturnDialogOpen}
      onClose={handleCloseAvaturnDialog}
      fullWidth
      maxWidth={false}
      PaperProps={{
        className: 'vica-avaturn-dialog-paper',
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: '#0f3d56' }}>
        My Own Avatar Creator
      </DialogTitle>
      <DialogContent
        className="vica-avaturn-dialog-content"
        sx={{ background: 'linear-gradient(180deg, rgba(236,247,255,0.58), #ffffff)' }}
      >
        <p className="text-sm text-[#355c71] mb-3">
          Follow the selfie flow, customize your avatar, then click <strong>Generate GLB &amp; Save</strong>.
        </p>
        <div
          ref={avaturnContainerRef}
          id="avaturn-sdk-container"
          className="vica-avaturn-embed"
          style={{
            width: '100%',
          }}
        />
        {avaturnStatus && (
          <p className="text-xs text-[#355c71] mt-3 mb-0">{avaturnStatus}</p>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <button
          className="vica-btn-ghost vica-btn-md"
          onClick={handleOpenAvaturnExternally}
          disabled={isImportingAvatar || isExportingAvatar}
        >
          Open Avaturn Site
        </button>
        <button
          className="vica-btn vica-btn-md"
          onClick={handleRunAvaturnExport}
          disabled={!isAvaturnReady || isImportingAvatar || isExportingAvatar}
        >
          {isExportingAvatar
            ? 'Generating...'
            : isImportingAvatar
              ? 'Saving...'
              : 'Generate GLB & Save'}
        </button>
        <button
          className="vica-btn-secondary vica-btn-md"
          onClick={handleCloseAvaturnDialog}
          disabled={isImportingAvatar || isExportingAvatar}
        >
          Close
        </button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default EnvironmentSettings;
